// /* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect} from 'react'
import {useIntl} from 'react-intl'
import {Link, useNavigate} from 'react-router-dom'
import {PreviewCard} from '../../../common/component/PreviewCard'
import { SettingOption } from '../../../common/component/DropdownMenu'
import { CustomInputRenderer } from '../../../common/component/CustomInputRenderer'
import { parseLocalDate } from '../../../util/Locale'
import { SubSectionHeader } from '../../../common/component/SubSectionHeader'
import { IContactDto } from '../Contact.model'

interface Props {
  data?: IContactDto
  loading: boolean
  setRowSelected: (rowId?: string) => void
}

export const ContactPreview: FC<Props> = ({data, loading, setRowSelected}: Props) => {
  const intl = useIntl()
  const navigate = useNavigate()
  const settingOptions: SettingOption[] = [
    {
      label: intl.formatMessage({id: 'UTIL.DETAILS'}),
      icon: 'bi-book',
      action: () => navigate(`/contacts/${data?.id}`),
    },
  ]

  useEffect(() => {
    if (data && !data.userId) {
      settingOptions.push({
        label: intl.formatMessage({id: 'UTIL.EDIT'}),
        icon: 'bi-pencil',
        action: () => navigate(`/contacts/${data?.id}/edit`),
      })
    }
  }, [data])

  return (
    <PreviewCard
      loading={loading}
      title={data?.displayName}
      subTitle={data?.sequence}
      pathOnClickHandler={`/contacts/${data?.id}`}
      closeOnClickHandler={() => setRowSelected(undefined)}
      settingOptions={settingOptions}
      content={
        data && (
          <div>
            <div className='row my-10'>
              <SubSectionHeader
                title={intl.formatMessage({id: 'UTIL.GENERAL'})}
                separator={false}
              />

              <div className='px-5'>
                <div className='row mb-5'>
                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.UPDATED_DATE'})}
                    value={parseLocalDate(data.updatedAt, intl.formatMessage({id: 'I18N.LOCALE'}))}
                  ></CustomInputRenderer>

                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.ID'})}
                    value={`${data.sequence}`}
                  ></CustomInputRenderer>

                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.DISPLAY_NAME'})}
                    object={
                      <span className='fs-6 ms-3 text-dark'>
                        <Link className='' to={`/contacts/${data.id}`}>
                          {data.displayName}
                        </Link>
                      </span>
                    }
                  ></CustomInputRenderer>

                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.EMAIL'})}
                    value={`${data.email ?? ''}`}
                  ></CustomInputRenderer>

                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.TELEPHONE'})}
                    value={`${data.telephone ?? ''}`}
                  ></CustomInputRenderer>
                </div>
              </div>

              {data.details && data.details.length > 0 && (
                <>
                  <SubSectionHeader title={intl.formatMessage({id: 'UTIL.ADDITIONAL_DETAILS'})} />

                  <div className='px-5'>
                    <div className='row mb-5'>
                      {data.details &&
                        data.details.map((detail, index) => (
                          <div key={index}>
                            <CustomInputRenderer
                              label={intl.formatMessage({id: `UTIL.${detail.type}`})}
                              value={`${detail.value ?? ''}`}
                            ></CustomInputRenderer>
                          </div>
                        ))}
                    </div>
                  </div>
                </>
              )}

              <SubSectionHeader
                separator={false}
                title={intl.formatMessage({id: 'UTIL.LOCATION'})}
              />

              <div className='px-5'>
                <div className='row mb-5'>
                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.COUNTRY'})}
                    value={
                      data.countryCode
                        ? intl.formatMessage({
                            id: `DROPDOWN.COUNTRY.${data.countryCode}`,
                          })
                        : ''
                    }
                  ></CustomInputRenderer>

                  <CustomInputRenderer
                    label={intl.formatMessage({id: 'UTIL.ADDRESS'})}
                    value={data.address}
                  ></CustomInputRenderer>
                </div>
              </div>
            </div>
          </div>
        )
      }
    ></PreviewCard>
  )
}
